import React, { useState } from "react";
import { Box, AppBar, Toolbar, styled, Typography, Drawer, List, ListItem, Divider, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { useTheme as useCustomTheme } from '../../theme/theme-provider';
import { Facebook, Instagram, Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link as RouterLink } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { SoundCloudIcon } from "../custom-icons/Soundcloud.jsx";
import { SpotifyIcon } from "../custom-icons/Spotify.jsx";
import { AlanDIcon } from "../custom-icons/Aland.jsx";
import "./Navbar.css";
import ThemeToggle from "../../theme/theme-toggle";

const NAV_LINK_STYLE = {
    cursor: "pointer",
    color: "#F2D399",
    textDecoration: "none",
    fontFamily: '"Courier New", monospace',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: "16px",
    letterSpacing: '0.06em',
    borderBottom: '2px solid transparent',
    paddingBottom: '2px',
    transition: 'color 0.2s, border-color 0.2s',
    '&:hover': {
        color: "white",
        borderBottom: '2px solid white',
    },
};

const DRAWER_LINK_STYLE = {
    cursor: "pointer",
    color: "#F2D399",
    textDecoration: "none",
    fontFamily: '"Courier New", monospace',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: "18px",
    display: 'block',
    width: '100%',
    textAlign: 'center',
    py: 0.5,
};

const StyledDesktopLink = styled(RouterLink)(NAV_LINK_STYLE);
const StyledDesktopScrollLink = styled(ScrollLink)(NAV_LINK_STYLE);
const StyledDrawerLink = styled(RouterLink)(DRAWER_LINK_STYLE);
const StyledDrawerScrollLink = styled(ScrollLink)(DRAWER_LINK_STYLE);

const SOCIAL_ICONS_SX = {
    cursor: "pointer",
    color: 'white',
    bgcolor: "black",
    borderRadius: '50%',
    p: '3px',
    fontSize: 22,
    '&:hover': { color: "black", bgcolor: "white" }
};

const getActiveLinkStyle = (drawer, isActive, isDark) => {
    if (!isActive) {
        return undefined;
    }

    return drawer
        ? {
            color: isDark ? '#FFFFFF' : '#1c1a18',
            backgroundColor: isDark ? 'rgba(242, 211, 153, 0.14)' : 'rgba(47, 76, 76, 0.10)',
            borderRadius: '6px',
        }
        : {
            color: isDark ? '#FFFFFF' : '#1c1a18',
            borderBottom: isDark ? '2px solid #F2D399' : '2px solid #2f4c4c',
        };
};

const Navbar = ({ page }) => {
    const theme = useTheme();
    const isMediumHeader = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { themeMode } = useCustomTheme();
    const isDark = themeMode === 'dark';

    const MenuItems = [
        { LinkType: 'Scroll', Name: 'Releases', Link: 'Releases', pageKey: null },
        { LinkType: 'Router', Name: 'Work for Brands', Link: '/work-for-brands/', pageKey: 'brands' },
        { LinkType: 'Router', Name: 'Services', Link: '/services/', pageKey: 'services' },
        { LinkType: 'Router', Name: 'About', Link: '/about/', pageKey: 'about' },
        { LinkType: 'Router', Name: 'Contact', Link: '/contact/', pageKey: 'contact' },
    ];

    const MenuItemsDetailPage = [
        { LinkType: 'Router', Name: 'Home', Link: '/', pageKey: 'home' },
        { LinkType: 'Router', Name: 'Releases', Link: '/#Releases', pageKey: null },
        { LinkType: 'Router', Name: 'Work for Brands', Link: '/work-for-brands/', pageKey: 'brands' },
        { LinkType: 'Router', Name: 'Services', Link: '/services/', pageKey: 'services' },
        { LinkType: 'Router', Name: 'About', Link: '/about/', pageKey: 'about' },
        { LinkType: 'Router', Name: 'Contact', Link: '/contact/', pageKey: 'contact' },
    ];

    const isHomePage = page === "home" || page === "";
    const activeItems = isHomePage ? MenuItems : MenuItemsDetailPage;

    const isItemActive = (item) => item.pageKey && item.pageKey === page;

    const renderLink = (item, index, drawer = false) => {
        const RouterLinkComp = drawer ? StyledDrawerLink : StyledDesktopLink;
        const ScrollLinkComp = drawer ? StyledDrawerScrollLink : StyledDesktopScrollLink;
        const activeStyle = getActiveLinkStyle(drawer, isItemActive(item), isDark);
        const linkSx = drawer ? {
            color: isDark ? '#F2D399' : '#2f4c4c',
            '&:hover': { color: isDark ? 'white' : '#000', borderBottomColor: isDark ? 'white' : '#000' },
        } : {
            color: isDark ? '#F2D399' : '#2f4c4c',
            '&:hover': { color: isDark ? 'white' : '#000', borderBottomColor: isDark ? 'white' : '#000' },
        };
        if (item.LinkType === 'Router') {
            return (
                <RouterLinkComp
                    key={index}
                    to={item.Link}
                    onClick={drawer ? () => setDrawerOpen(false) : undefined}
                    style={activeStyle}
                    sx={linkSx}
                    aria-current={isItemActive(item) ? 'page' : undefined}
                >
                    {item.Name}
                </RouterLinkComp>
            );
        }
        return (
            <ScrollLinkComp
                key={index}
                to={item.Link}
                spy smooth offset={50} duration={500}
                onClick={drawer ? () => setDrawerOpen(false) : undefined}
                style={activeStyle}
                sx={linkSx}
                aria-current={isItemActive(item) ? 'page' : undefined}
            >
                {item.Name}
            </ScrollLinkComp>
        );
    };

    const SocialIcons = () => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <SpotifyIcon sx={{ fontSize: 22, cursor: 'pointer', color: 'white', bgcolor: 'black', borderRadius: '50%', p: '3px', '&:hover': { color: 'black', bgcolor: 'white' } }} onClick={() => { window.location.href = 'https://open.spotify.com/artist/1l2XNMmYyIIMOIFY4HFsNU'; }} />
            <SoundCloudIcon sx={{ fontSize: 22, cursor: 'pointer', color: 'white', bgcolor: 'black', borderRadius: '50%', p: '3px', '&:hover': { color: 'black', bgcolor: 'white' } }} onClick={() => { window.location.href = 'https://soundcloud.com/distorise'; }} />
            <Facebook onClick={() => { window.location.href = 'https://www.facebook.com/distorise/'; }} sx={SOCIAL_ICONS_SX} />
            <Instagram onClick={() => { window.location.href = 'https://www.instagram.com/alan_distorise/'; }} sx={SOCIAL_ICONS_SX} />
        </Box>
    );

    return (
        <AppBar sx={{ background: isDark ? "#261B1A" : "#fffaf4", color: isDark ? '#fff' : '#1c1a18' }} position="static">
            <Toolbar sx={{ minHeight: { xs: 56, md: 64 }, px: { xs: 1.5, md: 3 }, gap: 0, position: 'relative' }}>

                {/* Mobile — icon-only logo on the left */}
                <RouterLink
                    to="/"
                    style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                        }}
                    >
                        <AlanDIcon sx={{ fontSize: 28, color: '#F2D399', display: 'block' }} className="AlanDIcon" />
                    </Box>
                </RouterLink>

                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <RouterLink to="/" style={{ textDecoration: 'none', color: 'inherit', flexShrink: 0 }}>
                        <Box sx={{
                            fontFamily: "Light-1",
                            letterSpacing: { xs: "3px", md: isMediumHeader ? "4px" : "5px" },
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            color: isDark ? 'white' : '#1c1a18',
                            fontSize: { xs: 16, md: isMediumHeader ? 18 : 20 },
                            lineHeight: 1,
                        }}>
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>DIST</Box>
                            <AlanDIcon sx={{ fontSize: { xs: 22, md: isMediumHeader ? 22 : 26 }, color: '#F2D399', mx: 0.5, display: 'block' }} className="AlanDIcon" />
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>RISE</Box>
                        </Box>
                    </RouterLink>
                </Box>

                {/* Desktop — centred nav links */}
                <Box sx={{
                    display: { xs: 'none', md: 'flex' },
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: { md: isMediumHeader ? 1.5 : 3, lg: 3 },
                    flexWrap: 'wrap',
                }}>
                    {activeItems.map((item, i) => renderLink(item, i))}
                </Box>

                {/* Desktop — theme toggle + social icons, right-aligned */}
                <Box sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    gap: 1,
                    flexShrink: 0,
                    ml: 2,
                }}>
                    <ThemeToggle />
                    <Box sx={{ display: { md: 'none', lg: 'flex' } }}>
                        <SocialIcons />
                    </Box>
                </Box>

                {/* Mobile — centered hamburger */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, position: 'absolute', left: '50%', transform: 'translateX(-50%)', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton
                        color="inherit"
                        onClick={() => setDrawerOpen(true)}
                        aria-label="Open menu"
                        sx={{ p: 1 }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Box>

                {/* Mobile — theme toggle anchored right */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
                    <ThemeToggle />
                </Box>
            </Toolbar>

            {/* Mobile drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '78vw', sm: 320 },
                        background: isDark ? "#261B1A" : "#fffaf4",
                        color: isDark ? '#F2D399' : '#2f4c4c',
                        display: 'flex',
                        flexDirection: 'column',
                    }
                }}
            >
                {/* Drawer header */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, pt: 1.5, pb: 1 }}>
                    <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: isDark ? '#F2D399' : '#2f4c4c', mb: 0.5 }} aria-label="Close menu">
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ fontFamily: "Light-1", color: isDark ? 'white' : '#1c1a18', fontSize: 18, letterSpacing: '3px', display: 'flex', alignItems: 'center', lineHeight: 1 }}>
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>DIST</Box>
                        <AlanDIcon sx={{ fontSize: 22, color: '#F2D399', mx: 0.5, display: 'block' }} className="AlanDIcon" />
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>RISE</Box>
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: isDark ? 'rgba(242,211,153,0.2)' : 'rgba(0,0,0,0.12)' }} />

                {/* Nav links */}
                <List sx={{ px: 2, pt: 1, flex: 1 }}>
                    {activeItems.map((item, i) => (
                        <ListItem key={i} sx={{ px: 0, py: 0.5, justifyContent: 'center' }}>
                            {renderLink(item, i, true)}
                        </ListItem>
                    ))}
                </List>

                <Divider sx={{ borderColor: isDark ? 'rgba(242,211,153,0.2)' : 'rgba(0,0,0,0.12)' }} />

                {/* Social icons + theme toggle at bottom of drawer */}
                <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <SocialIcons />
                    <ThemeToggle />
                </Box>
            </Drawer>
        </AppBar>
    );
};

export default Navbar;